// Autonomous drone mission planner.
// Advances in-flight missions (drops fire on arrival) and delegates NEW
// mission planning to a pluggable strategy from droneStrategies.js — the
// same code path drives both the live simulation and Strategy Lab
// experiments, so what you A/B test is exactly what deploys.

import {
  DRONE_STRATEGIES,
  DEFAULT_STRATEGY_PARAMS,
  collectPlanningCells,
} from "./droneStrategies";

export const DRONE_SPEED_TILES_PER_HOUR = 90;
export const DRONE_PLANNING_INTERVAL_HRS = 0.5;

const dist = (a, b) => Math.hypot(a.row - b.row, a.col - b.col);

// Returns { missions, drops } where drops are retardant actions to apply now.
export const updateDroneMissions = ({
  engine,
  stations,
  missions,
  nowMs,
  structureValue,
  weather,
  strategyId = "nearest",
  params = {},
}) => {
  const drops = [];
  const updated = [];

  // Advance in-flight missions; queue drops on arrival
  for (const m of missions) {
    if (m.status !== "deploying") {
      updated.push(m);
      continue;
    }
    const hours = (nowMs - m.startTime) / 3600000;
    const covered = hours * DRONE_SPEED_TILES_PER_HOUR;
    if (covered >= dist(m.origin, m.target)) {
      drops.push({ row: m.target.row, col: m.target.col });
      updated.push({ ...m, status: "arrived" });
    } else {
      updated.push(m);
    }
  }

  const strategy = DRONE_STRATEGIES[strategyId];
  if (!strategy || strategyId === "none" || !stations?.length) {
    return { missions: updated, drops };
  }

  const { fireCells, infraCells } = collectPlanningCells(engine);
  if (fireCells.length === 0 || infraCells.length === 0) {
    return { missions: updated, drops };
  }

  const busyOrigins = new Set(
    updated
      .filter((m) => m.status === "deploying")
      .map((m) => `${m.origin.row},${m.origin.col}`)
  );
  const available = stations.filter(
    (s) => !busyOrigins.has(`${s.row},${s.col}`)
  );
  if (available.length === 0) return { missions: updated, drops };

  const assignments = strategy.plan({
    engine,
    stations: available,
    fireCells,
    infraCells,
    structureValue,
    weather,
    params: { ...DEFAULT_STRATEGY_PARAMS, ...params },
  });

  for (const a of assignments) {
    updated.push({
      id: `drone-${a.origin.row}-${a.origin.col}-${nowMs}`,
      origin: a.origin,
      target: a.target,
      startTime: nowMs,
      status: "deploying",
    });
  }

  return { missions: updated, drops };
};
